import classNames from 'classnames';
import { forwardRef, useRef } from 'react';

import OverlayTrigger from '../OverlayTrigger';
import Spinner from '../Spinner';
import Tooltip from '../Tooltip';
import { type TooltipProps } from '../Tooltip';

import styles from './_index.module.scss';

interface IconButtonV2Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  isLoading?: boolean;
  // eslint-disable-next-line perfectionist/sort-union-types
  size?: 'small' | 'medium' | 'large';
  tooltip?: TooltipProps['children'];
  tooltipProps?: TooltipProps;
  variant?: 'default' | 'subtle';
}

const IconButtonV2 = <T extends React.ElementType>(
  {
    'aria-label': ariaLabel,
    as,
    children,
    className,
    icon,
    isLoading,
    onClick,
    size = 'medium',
    tooltip,
    tooltipProps,
    variant = 'default',
    ...props
  }: IconButtonV2Props & PolymorphicProps<T>,
  parentRef?: React.ForwardedRef<HTMLButtonElement>
) => {
  const localRef = useRef(null);

  const buttonStyles = [
    styles[`icon-button-${variant}`],
    styles[`icon-button-size-${size}`],
    { [styles['is-loading']]: isLoading },
  ];
  const Component = as || 'button';
  const ref = parentRef || localRef;
  const accessibleLabel = ariaLabel || (typeof tooltip === 'string' ? tooltip : undefined);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isLoading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <OverlayTrigger.Tooltip
      overlay={tooltip ? <Tooltip {...tooltipProps}>{tooltip}</Tooltip> : <></>}
      placement={tooltipProps?.placement}
    >
      <Component
        aria-label={accessibleLabel}
        className={classNames(...buttonStyles, className)}
        onClick={handleClick}
        ref={ref}
        {...props}
      >
        <div className={styles['icon-button-icon']}>{icon}</div>
        <Spinner className={styles['icon-button-spinner']} />
      </Component>
    </OverlayTrigger.Tooltip>
  );
};

const IconButtonV2WithRef = forwardRef(IconButtonV2);

export default IconButtonV2WithRef;
